import React, { useEffect } from 'react';

export function Card({
  children,
  style,
  onClick,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--color-surface-primary)',
        border: '1px solid var(--color-border-primary)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--space-6)',
        cursor: onClick ? 'pointer' : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Button({
  children,
  onClick,
  variant = 'primary',
  type = 'button',
  disabled,
  style,
  size = 'md',
}: {
  children: React.ReactNode;
  onClick?: (e?: React.MouseEvent) => void;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  type?: 'button' | 'submit';
  disabled?: boolean;
  style?: React.CSSProperties;
  size?: 'sm' | 'md';
}) {
  const variants: Record<string, React.CSSProperties> = {
    primary: { background: 'var(--color-brand-primary)', color: 'white', border: 'none' },
    secondary: {
      background: 'var(--color-surface-primary)',
      color: 'var(--color-text-primary)',
      border: '1px solid var(--color-border-primary)',
    },
    ghost: { background: 'transparent', color: 'var(--color-text-secondary)', border: 'none' },
    danger: { background: 'var(--color-feedback-error)', color: 'white', border: 'none' },
  };
  const padding = size === 'sm' ? 'var(--space-1) var(--space-3)' : 'var(--space-2) var(--space-4)';
  const fontSize = size === 'sm' ? 'var(--text-xs)' : 'var(--text-sm)';
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding,
        borderRadius: 'var(--radius-md)',
        fontSize,
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        ...variants[variant],
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function Input({
  value,
  onChange,
  placeholder,
  type = 'text',
  style,
  onKeyDown,
  autoFocus,
  min,
  max,
  step,
}: {
  value: string | number;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  style?: React.CSSProperties;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  autoFocus?: boolean;
  min?: number | string;
  max?: number | string;
  step?: number | string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      autoFocus={autoFocus}
      min={min}
      max={max}
      step={step}
      style={{
        padding: 'var(--space-2) var(--space-3)',
        border: '1px solid var(--color-border-primary)',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--text-sm)',
        color: 'var(--color-text-primary)',
        background: 'var(--color-surface-primary)',
        outline: 'none',
        ...style,
      }}
    />
  );
}

export function Textarea({
  value,
  onChange,
  placeholder,
  rows = 4,
  style,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  style?: React.CSSProperties;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        padding: 'var(--space-2) var(--space-3)',
        border: '1px solid var(--color-border-primary)',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--text-sm)',
        color: 'var(--color-text-primary)',
        background: 'var(--color-surface-primary)',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
        ...style,
      }}
    />
  );
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  style,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  style?: React.CSSProperties;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      style={{
        padding: 'var(--space-2) var(--space-3)',
        border: '1px solid var(--color-border-primary)',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--text-sm)',
        color: 'var(--color-text-primary)',
        background: 'var(--color-surface-primary)',
        outline: 'none',
        ...style,
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 'var(--space-6)',
        paddingBottom: 'var(--space-4)',
        borderBottom: '1px solid var(--color-border-primary)',
        gap: 'var(--space-4)',
        flexWrap: 'wrap',
      }}
    >
      <div>
        <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 }}>
          {title}
        </h1>
        {subtitle && (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', margin: 'var(--space-1) 0 0' }}>
            {subtitle}
          </p>
        )}
      </div>
      {action && <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>{action}</div>}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div style={{ textAlign: 'center', padding: 'var(--space-12) var(--space-4)' }}>
      <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0 }}>
        {title}
      </h3>
      {description && (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', margin: 'var(--space-2) 0 var(--space-4)' }}>
          {description}
        </p>
      )}
      {action}
    </div>
  );
}

export type BadgeColor = 'gray' | 'blue' | 'green' | 'yellow' | 'red' | 'purple';

export function Badge({ children, color = 'gray' }: { children: React.ReactNode; color?: BadgeColor }) {
  const colors: Record<BadgeColor, { bg: string; fg: string }> = {
    gray: { bg: 'var(--color-surface-tertiary)', fg: 'var(--color-text-secondary)' },
    blue: { bg: 'var(--color-brand-primary-light)', fg: 'var(--color-brand-primary)' },
    green: { bg: 'var(--color-feedback-success-light)', fg: 'var(--color-feedback-success)' },
    yellow: { bg: 'var(--color-feedback-warning-light)', fg: 'var(--color-feedback-warning)' },
    red: { bg: 'var(--color-feedback-error-light)', fg: 'var(--color-feedback-error)' },
    purple: { bg: 'rgba(139, 92, 246, 0.12)', fg: 'rgb(139, 92, 246)' },
  };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--text-xs)',
        fontWeight: 500,
        background: colors[color].bg,
        color: colors[color].fg,
      }}
    >
      {children}
    </span>
  );
}

export function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
      <div
        style={{
          width: 24,
          height: 24,
          border: '3px solid var(--color-border-primary)',
          borderTopColor: 'var(--color-brand-primary)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export function Modal({
  children,
  onClose,
  title,
  width = 500,
  footer,
}: {
  children: React.ReactNode;
  onClose: () => void;
  title: string;
  width?: number;
  footer?: React.ReactNode;
}) {
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: 'var(--space-4)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-surface-primary)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-6)',
          width: '100%',
          maxWidth: width,
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 600 }}>{title}</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 'var(--text-xl)',
              cursor: 'pointer',
              color: 'var(--color-text-tertiary)',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        {children}
        {footer && <div style={{ marginTop: 'var(--space-4)' }}>{footer}</div>}
      </div>
    </div>
  );
}

export function Drawer({
  children,
  onClose,
  title,
  width = 480,
}: {
  children: React.ReactNode;
  onClose: () => void;
  title: string;
  width?: number;
}) {
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: `min(${width}px, 100%)`,
          background: 'var(--color-surface-primary)',
          padding: 'var(--space-6)',
          overflow: 'auto',
          boxShadow: 'var(--shadow-xl)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 600 }}>{title}</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 'var(--text-xl)',
              cursor: 'pointer',
              color: 'var(--color-text-tertiary)',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{hint}</span>}
    </label>
  );
}

export function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)', marginTop: 2 }}>{children}</div>
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { value: T; label: string; count?: number }[];
  active: T;
  onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-1)', borderBottom: '1px solid var(--color-border-primary)', marginBottom: 'var(--space-4)' }}>
      {tabs.map((t) => (
        <button
          key={t.value}
          onClick={() => onChange(t.value)}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: 'var(--text-sm)',
            fontWeight: active === t.value ? 600 : 500,
            color: active === t.value ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
            borderBottom: active === t.value ? '2px solid var(--color-brand-primary)' : '2px solid transparent',
            marginBottom: -1,
          }}
        >
          {t.label}
          {typeof t.count === 'number' && (
            <span style={{ marginLeft: 'var(--space-2)', color: 'var(--color-text-tertiary)' }}>{t.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
      <span
        onClick={() => onChange(!checked)}
        style={{
          display: 'inline-block',
          width: 36,
          height: 20,
          borderRadius: 'var(--radius-full)',
          background: checked ? 'var(--color-brand-primary)' : 'var(--color-surface-tertiary)',
          position: 'relative',
          transition: 'background 0.2s',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 18 : 2,
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: 'white',
            boxShadow: 'var(--shadow-sm)',
            transition: 'left 0.2s',
          }}
        />
      </span>
      {label && <span style={{ fontSize: 'var(--text-sm)' }}>{label}</span>}
    </label>
  );
}

export function formatCents(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
}

export function formatDate(s: string): string {
  return new Date(s).toLocaleDateString();
}

export function formatDateTime(s: string): string {
  return new Date(s).toLocaleString();
}

export function ErrorBanner({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div
      style={{
        background: 'var(--color-feedback-error-light)',
        color: 'var(--color-feedback-error)',
        padding: 'var(--space-2) var(--space-3)',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--text-sm)',
        marginBottom: 'var(--space-3)',
      }}
    >
      {error}
    </div>
  );
}
